const API_BASE = '/api';
const HUB_TOKEN_KEY = 'gospelwise_handoff_token';

let inMemoryHubToken = '';

const getStoredHubToken = () => {
  try {
    const sessionToken = sessionStorage.getItem(HUB_TOKEN_KEY);
    if (sessionToken) return sessionToken;
  } catch (error) {
    console.warn('Unable to read the Hub token from session storage:', error);
  }

  try {
    const persistentToken = localStorage.getItem(HUB_TOKEN_KEY);
    if (persistentToken) return persistentToken;
  } catch (error) {
    console.warn('Unable to read the Hub token from local storage:', error);
  }

  return inMemoryHubToken;
};

const storeHubToken = (token) => {
  inMemoryHubToken = token;

  try {
    sessionStorage.setItem(HUB_TOKEN_KEY, token);
  } catch (error) {
    console.warn('Unable to store the Hub token in session storage:', error);
  }

  try {
    localStorage.setItem(HUB_TOKEN_KEY, token);
  } catch (error) {
    console.warn('Unable to store the Hub token in local storage:', error);
  }
};

const adminRequest = async (url, options = {}) => {
  const token = getStoredHubToken();
  if (!token) {
    throw new Error('Please launch FormatterPro from the Hub to verify your admin session.');
  }

  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${token}`);
  // Backup copy of the token: NameHero/LiteSpeed can strip Authorization,
  // but never strips this custom header. admin-users.php accepts either.
  headers.set('X-Hub-Token', token);

  return fetch(url, {
    ...options,
    credentials: 'same-origin',
    headers
  });
};

/**
 * Robust response handler for Spoke-side API calls
 */
const handleResponse = async (response) => {
  const text = await response.text();
  try {
    const data = JSON.parse(text);
    if (!response.ok) throw new Error(data.error || `Server Error ${response.status}`);
    return data;
  } catch (e) {
    if (e instanceof SyntaxError) {
      console.error("Malformed JSON response from Spoke API:", text);
      throw new Error("Invalid server response from Spoke");
    }
    throw e;
  }
};

/**
 * Safely decodes JWT payload on the client without external library
 */
const parseJwtPayload = (token) => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.warn("Failed to decode token payload:", e);
    return null;
  }
};

//============================================================================
// AUTHENTICATION SERVICE - JWT Token Handoff
//============================================================================
export const authService = {
  verifyToken: async (token) => {
    try {
      const response = await fetch(`https://www.gospelwiseauthor.app/api/auth-verify.php?app=formatpro&token=${encodeURIComponent(token)}`);
      const data = await response.json().catch(() => null);

      if (response.ok && data?.valid === true) {
        storeHubToken(token);
        return data;
      }

      if (response.status === 403 || data?.error === 'Account is pending') {
        return {
          valid: false,
          status: 403,
          error: data?.error || 'Account is pending',
          message: 'GospelWise Hub rejected this account as pending (HTTP 403). The Hub administrator must approve this account before FormatterPro can be accessed.'
        };
      }

      return {
        valid: false,
        status: response.status,
        error: data?.error || 'Verification failed',
        message: `GospelWise Hub verification rejected (HTTP ${response.status}): ${data?.error || 'Token expired or invalid'}`
      };
    } catch (err) {
      console.warn("Network error during Hub verification:", err);
      return {
        valid: false,
        status: 0,
        error: 'Network error',
        message: 'Could not connect to GospelWise Hub for authentication verification. Please check your internet connection.'
      };
    }
  },
  syncUser: async (userData) => {
    const response = await fetch(`${API_BASE}/user-sync.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });
    return handleResponse(response);
  },
  redeemCode: async (userId, code) => {
    const response = await fetch(`${API_BASE}/redeem-code.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, code })
    });
    return handleResponse(response);
  }
};

//============================================================================
// DATA SERVICE - Book & Module Management
//============================================================================
export const dataService = {
  getBooks: async (userId) => {
    if (!userId) return [];
    const response = await fetch(`${API_BASE}/books.php?user_id=${userId}`);
    return handleResponse(response);
  },
  saveBook: async (userId, bookData) => {
    const response = await fetch(`${API_BASE}/books.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, ...bookData })
    });
    return handleResponse(response);
  },
  deleteBook: async (bookId) => {
    const response = await fetch(`${API_BASE}/books.php?id=${bookId}`, {
      method: 'DELETE'
    });
    return handleResponse(response);
  },
  getModuleData: async (projectId) => {
    const response = await fetch(`${API_BASE}/module-data.php?project_id=${projectId}`);
    return handleResponse(response);
  },
  saveModuleData: async (projectId, section, data) => {
    const response = await fetch(`${API_BASE}/module-data.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project_id: projectId,
        section_name: section,
        content_json: JSON.stringify(data)
      })
    });
    return handleResponse(response);
  }
};

//============================================================================
// FORMAT SERVICE - FormatterPro Manuscript Processing
//============================================================================
export const formatService = {
  uploadManuscript: async (formData) => {
    const response = await fetch(`${API_BASE}/upload-manuscript.php`, {
      method: 'POST',
      body: formData
    });
    return handleResponse(response);
  },
  updateManuscript: async (formData) => {
    const response = await fetch(`${API_BASE}/update-manuscript.php`, {
      method: 'POST',
      body: formData
    });
    return handleResponse(response);
  },
  analyzeManuscript: async (manuscriptId) => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let timeoutId;
    let timeoutReject;
    const timeout = new Promise((resolve, reject) => {
      timeoutReject = reject;
      timeoutId = setTimeout(() => {
        controller?.abort();
        reject(new Error('Manuscript analysis timed out after 45 seconds. Retry; if it repeats, share the manuscript ID and approximate time with support.'));
      }, 45000);
    });

    const request = (async () => {
      const response = await fetch(`${API_BASE}/analyze-manuscript.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        ...(controller ? { signal: controller.signal } : {}),
        body: JSON.stringify({ manuscript_id: manuscriptId })
      });
      const result = await handleResponse(response);
      const structureIsValid = ['front_matter', 'chapters', 'back_matter']
        .every(section => Array.isArray(result?.structure?.[section]));

      if (!result?.success || !result.analysis_report || !structureIsValid) {
        throw new Error(result?.error || `Analysis response was incomplete${result?.request_id ? ` (request ${result.request_id})` : ''}. Retry the analysis.`);
      }
      return result;
    })();

    try {
      return await Promise.race([request, timeout]);
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Manuscript analysis was interrupted by the browser. Retry the analysis. If it happens again, share the manuscript ID and approximate time with support.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      timeoutReject = null;
    }
  },
  fixManuscript: async (manuscriptId) => {
    const response = await fetch(`${API_BASE}/fix-manuscript.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ manuscript_id: manuscriptId })
    });
    return handleResponse(response);
  },
  buildEpub: async (manuscriptId, config) => {
    const response = await fetch(`${API_BASE}/build-epub.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ manuscript_id: manuscriptId, ...config })
    });
    return handleResponse(response);
  },
  buildPdf: async (manuscriptId, config) => {
    const response = await fetch(`${API_BASE}/build-pdf.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ manuscript_id: manuscriptId, ...config })
    });
    return handleResponse(response);
  },
  deleteManuscript: async (manuscriptId) => {
    const response = await fetch(`${API_BASE}/delete-manuscript.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ manuscript_id: manuscriptId })
    });
    return handleResponse(response);
  }
};

//============================================================================
// ADMIN SERVICE - User & Tier Management & Redemption Codes
//============================================================================
export const adminService = {
  getUsers: async () => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`);
    return handleResponse(response);
  },
  updateAccess: async (adminUserId, targetUserId, accessType, singleAmt = null) => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_user_id: adminUserId,
        target_user_id: targetUserId,
        access_type: accessType,
        single_amt: singleAmt
      })
    });
    return handleResponse(response);
  },
  /**
   * Permanently delete a user and all their manuscripts, analyses, exports and files.
   * confirmEmail must match the target user's email exactly, or the server refuses.
   */
  deleteUser: async (adminUserId, targetUserId, confirmEmail) => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_user_id: adminUserId,
        action: 'delete_user',
        target_user_id: targetUserId,
        confirm_email: confirmEmail
      })
    });
    return handleResponse(response);
  },
  /**
   * Permanently delete one project (manuscript) with its analyses, exports and files.
   * The user account is kept.
   */
  deleteProject: async (adminUserId, manuscriptId) => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_user_id: adminUserId,
        action: 'delete_project',
        manuscript_id: manuscriptId
      })
    });
    return handleResponse(response);
  },
  /**
   * Lock or unlock a project's Book Metadata.
   * Locked projects can still replace the manuscript file, change structure and print format, and export.
   */
  setProjectLock: async (adminUserId, manuscriptId, locked) => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_user_id: adminUserId,
        action: 'set_project_lock',
        manuscript_id: manuscriptId,
        locked: !!locked
      })
    });
    return handleResponse(response);
  },
  getSettings: async () => {
    const response = await adminRequest(`${API_BASE}/admin-users.php?action=settings`);
    return handleResponse(response);
  },
  saveSettings: async (adminUserId, settings) => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_user_id: adminUserId,
        action: 'save_settings',
        ...settings
      })
    });
    return handleResponse(response);
  },
  getAccessCodes: async () => {
    const response = await adminRequest(`${API_BASE}/admin-users.php?action=get_codes`);
    return handleResponse(response);
  },
  addAccessCode: async (adminUserId, codeData) => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_user_id: adminUserId,
        action: 'add_code',
        ...codeData
      })
    });
    return handleResponse(response);
  },
  updateAccessCode: async (adminUserId, codeData) => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_user_id: adminUserId,
        action: 'update_code',
        code_id: codeData.code_id,
        ...codeData
      })
    });
    return handleResponse(response);
  },
  toggleAccessCode: async (adminUserId, codeId, isActive) => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_user_id: adminUserId,
        action: 'toggle_code',
        code_id: codeId,
        is_active: isActive ? 1 : 0
      })
    });
    return handleResponse(response);
  },
  deleteAccessCode: async (adminUserId, codeId) => {
    const response = await adminRequest(`${API_BASE}/admin-users.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        admin_user_id: adminUserId,
        action: 'delete_code',
        code_id: codeId
      })
    });
    return handleResponse(response);
  }
};